import { useStore } from '@tanstack/react-form';
import { Checkbox, Input, LearnMoreLink, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import z from 'zod';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { useAppForm } from '../../../../lib/app-form';
import { PARTNER_DOCS_URLS } from '../../constants';
import type { PartnerModalProps } from '../../types';
import { CredentialField } from '../credential-field';
import { usePartnerModalSave } from './shared';

const UNLEASHED = 'features:partners.components.unleashed' satisfies I18nPrefix;
const COMMON = 'features:partners.components.common' satisfies I18nPrefix;

const TIER_KEYS = [
  'tier1',
  'tier2',
  'tier3',
  'tier4',
  'tier5',
  'tier6',
  'tier7',
  'tier8',
  'tier9',
  'tier10',
] as const;

const EMPTY_TIER_CODES: Record<string, string> = Object.fromEntries(
  TIER_KEYS.map((key) => [key, '']),
);

export function UnleashedModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);

  const storedTierCodes = settings.integrationUnleashedSellPriceTierExpectedCurrencyCodes;
  const hasStoredApiKey = !!settings.integrationUnleashedApiKey;
  const apiKeyValidator = hasStoredApiKey ? z.string() : z.string().trim().min(1);

  // Settings the API has never persisted come back `undefined`, so each one needs a fallback
  // before it seeds a controlled input — and before the `onSubmit` schema rejects it.
  const form = useAppForm({
    defaultValues: {
      apiId: settings.integrationUnleashedApiId ?? '',
      apiKey: '',
      customerTypes: settings.integrationUnleashedCustomerTypes ?? '',
      prefixedPriceLists: settings.integrationUnleashedPrefixedPriceLists ?? '',
      sendInvite: settings.integrationUnleashedSendInvite ?? false,
      showSellPriceTierCurrencyConfig: storedTierCodes
        ? Object.values(storedTierCodes).some((value) => value !== null)
        : false,
      tierCurrencyCodes: storedTierCodes
        ? { ...EMPTY_TIER_CODES, ...storedTierCodes }
        : EMPTY_TIER_CODES,
    },
    // Only the credentials are mandatory: customer types and price lists are filters, and merchants
    // who don't use them have always been able to save the integration with them empty.
    validators: {
      onSubmit: z.object({
        apiId: z.string().trim().min(1),
        apiKey: apiKeyValidator,
        customerTypes: z.string(),
        prefixedPriceLists: z.string(),
        sendInvite: z.boolean(),
        showSellPriceTierCurrencyConfig: z.boolean(),
        tierCurrencyCodes: z.record(z.string(), z.string()),
      }),
    },
    onSubmit: ({ value }) => {
      const customerTypes = value.customerTypes
        .split(',')
        .map((type) => type.trim())
        .filter((type) => type !== '')
        .join(',');

      const tierCurrencyCodes = TIER_KEYS.reduce(
        (acc, key) => {
          const currency = value.tierCurrencyCodes[key] ?? '';
          acc[key] =
            value.showSellPriceTierCurrencyConfig && currency ? currency.toUpperCase().trim() : '';
          return acc;
        },
        {} as Record<(typeof TIER_KEYS)[number], string>,
      );

      const payload: Setting[] = [
        { key: 'integrationUnleashed', value: true },
        { key: 'integrationUnleashedCustomerTypes', value: customerTypes },
        { key: 'integrationUnleashedPrefixedPriceLists', value: value.prefixedPriceLists },
        { key: 'integrationUnleashedApiId', value: value.apiId },
        { key: 'integrationUnleashedSendInvite', value: value.sendInvite },
        {
          key: 'integrationUnleashedSellPriceTierExpectedCurrencyCodes',
          value: tierCurrencyCodes,
        },
      ];
      const apiKey = value.apiKey.trim();
      if (apiKey) {
        payload.push({ key: 'integrationUnleashedApiKey', value: apiKey });
      }
      void save(payload);
    },
  });

  const showTiers = useStore(form.store, (s) => s.values.showSellPriceTierCurrencyConfig);
  const tierCurrencyCodes = useStore(form.store, (s) => s.values.tierCurrencyCodes);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      secondaryAction={{ text: t(`${COMMON}.close`), onClick: onClose }}
      primaryAction={{
        text: t(`${COMMON}.save`),
        onClick: () => void form.handleSubmit(),
        loading: isSaving,
      }}
    >
      <Modal.Header>Unleashed</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <Text variant="secondary">
            {t(`${UNLEASHED}.description`)} <LearnMoreLink link={PARTNER_DOCS_URLS.unleashed} />
          </Text>
          <form.Field name="apiId">
            {(field) => (
              <Input
                label={t(`${UNLEASHED}.api_id.label`)}
                placeholder={t(`${UNLEASHED}.api_id.placeholder`)}
                tooltip={t(`${UNLEASHED}.api_id.tooltip`)}
                value={field.state.value}
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message}
              />
            )}
          </form.Field>
          <form.Field name="apiKey">
            {(field) => (
              <CredentialField
                label={t(`${UNLEASHED}.api_key.label`)}
                placeholder={t(`${UNLEASHED}.api_key.placeholder`)}
                tooltip={t(`${UNLEASHED}.api_key.tooltip`)}
                value={field.state.value}
                onChange={field.handleChange}
                hasStoredValue={hasStoredApiKey}
                error={field.state.meta.errors[0]?.message}
              />
            )}
          </form.Field>
          <form.Field name="customerTypes">
            {(field) => (
              <Input
                label={t(`${UNLEASHED}.customer_types.label`)}
                placeholder={t(`${UNLEASHED}.customer_types.placeholder`)}
                tooltip={t(`${UNLEASHED}.customer_types.tooltip`)}
                value={field.state.value}
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message}
              />
            )}
          </form.Field>
          <form.Field name="prefixedPriceLists">
            {(field) => (
              <Input
                label={t(`${UNLEASHED}.price_lists.label`)}
                placeholder={t(`${UNLEASHED}.price_lists.placeholder`)}
                tooltip={t(`${UNLEASHED}.price_lists.tooltip`)}
                value={field.state.value}
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message}
              />
            )}
          </form.Field>
          <form.Field name="sendInvite">
            {(field) => (
              <Checkbox
                name="sendInviteEmail"
                label={t(`${UNLEASHED}.send_invite_label`)}
                description={t(`${COMMON}.send_invite_description`)}
                checked={field.state.value}
                onChange={() => field.handleChange(!field.state.value)}
              />
            )}
          </form.Field>
          <form.Field name="showSellPriceTierCurrencyConfig">
            {(field) => (
              <Checkbox
                label={t(`${UNLEASHED}.configure_currency.label`)}
                description={t(`${UNLEASHED}.configure_currency.description`)}
                checked={field.state.value}
                onChange={() => field.handleChange(!field.state.value)}
              />
            )}
          </form.Field>
          {showTiers && (
            <div className="flex flex-col gap-2">
              <Text variant="secondary">{t(`${UNLEASHED}.currency_blank_note`)}</Text>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {TIER_KEYS.map((key) => (
                  <Input
                    key={key}
                    name={`integrationUnleashedSellPriceTierExpectedCurrencyCodes[${key}]`}
                    label={t(`${UNLEASHED}.tier_currency_code_label`, {
                      tier: key.replace('tier', ''),
                    })}
                    placeholder="e.g: USD"
                    value={tierCurrencyCodes[key]}
                    onChange={(value) =>
                      form.setFieldValue('tierCurrencyCodes', (prev) => ({
                        ...prev,
                        [key]: value,
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
